import { AuditRecord } from './auditRecord';

/**
 * W8 — the durable audit store (spec section 5.8, BAR-SFR-02).
 *
 * IndexedDB, database `basil-audit`, object store `writes`, key path `[planId, chunkIndex]`, index
 * `by_ts`. IndexedDB rather than `localStorage` because the pre-write entry must survive a crash
 * and a reload and must be written inside a transaction whose completion the page can AWAIT.
 *
 * Fail closed: if the store cannot be opened or the pending entry cannot be committed — private
 * mode, quota, blocked site data — every method rejects with {@link AuditUnavailableError} and the
 * apply refuses with `audit-unavailable`; nothing is sent (E18).
 */

export const BASIL_AUDIT_DB_NAME = 'basil-audit';
export const BASIL_AUDIT_DB_VERSION = 1;
export const BASIL_AUDIT_STORE = 'writes';
export const BASIL_AUDIT_TS_INDEX = 'by_ts';

/** Thrown by every store method when the durable log is not usable. */
export class AuditUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuditUnavailableError';
    Object.setPrototypeOf(this, AuditUnavailableError.prototype);
  }
}

export interface AuditStore {
  /** Opens the database once and reports whether the durable log can be used at all. */
  available(): Promise<boolean>;
  /** Writes the `pending` entry and resolves only when its transaction has COMPLETED. */
  putPending(record: AuditRecord): Promise<void>;
  /** Replaces a record wholesale (the transitions are pure functions in `auditRecord.ts`). */
  put(record: AuditRecord): Promise<void>;
  /**
   * Reads ONE row inside a readwrite transaction, transitions it there and writes the result back.
   *
   * S-3b: the difference between this and `put` is the whole point of it. A sweep that lists rows,
   * decides about them and then puts the decided rows back is writing a SNAPSHOT, and the row may
   * have moved between the list and the write: the apply lane closes a `pending` entry the moment
   * the transport answers, so an attestation built from a stale copy could put `pending` plus a
   * settlement back over an entry that had since recorded a real response. Here the row is read
   * inside the transaction that writes it, the transition is applied to THAT row, and IndexedDB
   * serialises readwrite transactions on the store, so nothing can land between the two.
   *
   * @param transition applied to the row as the store holds it now; return `null` to write nothing.
   * @returns true when a row was written, false when the row was absent or the transition declined.
   */
  transitionRecord(
    planId: string,
    chunkIndex: number,
    transition: (record: AuditRecord) => AuditRecord | null,
  ): Promise<boolean>;
  /** Every record, oldest first by timestamp. */
  listAll(): Promise<AuditRecord[]>;
  /**
   * Entries left `pending`, `sent` or `unknown`: they block all new writes until a human resolves
   * them. An `unknown` outcome is the whole reason this list exists — the request may have landed,
   * and only a person reading the episode can say.
   *
   * S-3: `settled` is excluded, because a person HAS said. That is the only way an entry leaves
   * this list without the engine settling it, and it takes a writer's attestation to get there.
   */
  listUnresolved(): Promise<AuditRecord[]>;
  /**
   * Removes `reconciled`, `refused` and `settled` entries only.
   *
   * `pending`, `sent` and `unknown` are all left where they are: exporting the log is not the same
   * as settling what it records, and an unknown outcome must survive the export that reports it.
   * A `settled` entry is different in the one way that matters — the attestation has been made, and
   * the export the writer takes carries it — so the export may take it away with the rest.
   */
  clearExported(): Promise<number>;
  close(): void;
}

function requestAsPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(new AuditUnavailableError(`the audit store rejected a request: ${String(request.error)}`));
  });
}

function defaultFactory(): IDBFactory | null {
  try {
    if (typeof window === 'undefined' || !window.indexedDB) {
      return null;
    }

    return window.indexedDB;
  } catch (error) {
    return null;
  }
}

/**
 * Creates the audit store.
 *
 * @param factory the IndexedDB factory; defaults to `window.indexedDB`. `null` (private mode, a
 *  browser with site data blocked, a non-browser test environment) makes every method reject.
 */
export function createAuditStore(factory?: IDBFactory | null): AuditStore {
  const resolvedFactory = factory === undefined ? defaultFactory() : factory;
  let open: Promise<IDBDatabase> | null = null;
  let database: IDBDatabase | null = null;

  function openDatabase(): Promise<IDBDatabase> {
    if (open) {
      return open;
    }

    open = new Promise<IDBDatabase>((resolve, reject) => {
      if (!resolvedFactory) {
        reject(new AuditUnavailableError('IndexedDB is unavailable, so no write can be recorded'));

        return;
      }

      let request: IDBOpenDBRequest;

      try {
        request = resolvedFactory.open(BASIL_AUDIT_DB_NAME, BASIL_AUDIT_DB_VERSION);
      } catch (error) {
        reject(new AuditUnavailableError(`IndexedDB refused to open: ${String(error)}`));

        return;
      }

      request.onupgradeneeded = () => {
        const db = request.result;

        if (!db.objectStoreNames.contains(BASIL_AUDIT_STORE)) {
          const store = db.createObjectStore(BASIL_AUDIT_STORE, { keyPath: ['planId', 'chunkIndex'] });

          store.createIndex(BASIL_AUDIT_TS_INDEX, 'ts', { unique: false });
        }
      };
      request.onerror = () => reject(new AuditUnavailableError(`IndexedDB refused to open: ${String(request.error)}`));
      request.onblocked = () => reject(new AuditUnavailableError('IndexedDB is blocked by another tab'));
      request.onsuccess = () => {
        database = request.result;
        resolve(request.result);
      };
    });

    return open;
  }

  async function write(record: AuditRecord): Promise<void> {
    const db = await openDatabase();

    await new Promise<void>((resolve, reject) => {
      let transaction: IDBTransaction;

      try {
        transaction = db.transaction(BASIL_AUDIT_STORE, 'readwrite');
      } catch (error) {
        reject(new AuditUnavailableError(`the audit store could not start a transaction: ${String(error)}`));

        return;
      }

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(new AuditUnavailableError(`the audit write failed: ${String(transaction.error)}`));
      transaction.onabort = () => reject(new AuditUnavailableError(`the audit write was aborted: ${String(transaction.error)}`));

      try {
        transaction.objectStore(BASIL_AUDIT_STORE).put(record);
      } catch (error) {
        reject(new AuditUnavailableError(`the audit record was rejected: ${String(error)}`));
      }
    });
  }

  async function readAll(): Promise<AuditRecord[]> {
    const db = await openDatabase();
    const transaction = db.transaction(BASIL_AUDIT_STORE, 'readonly');
    const store = transaction.objectStore(BASIL_AUDIT_STORE);
    const rows = await requestAsPromise<AuditRecord[]>(store.getAll() as IDBRequest<AuditRecord[]>);

    return rows.slice().sort((left, right) => String(left.ts).localeCompare(String(right.ts)));
  }

  return {
    /**
     * Whether the durable log can be opened at all.
     *
     * T17-03: `false` means the database would not open, and it means NOTHING ELSE. A private
     * window causes it; so do cleared site data, an exhausted quota, a browser with site data
     * blocked, an extension, and a test environment with no factory. This method cannot tell them
     * apart and does not try, so the surface that reads it must not name one of them as the cause.
     * There is no browsing-mode probe in this client.
     */
    async available(): Promise<boolean> {
      try {
        await openDatabase();

        return true;
      } catch (error) {
        return false;
      }
    },

    putPending(record: AuditRecord): Promise<void> {
      return write(record);
    },

    put(record: AuditRecord): Promise<void> {
      return write(record);
    },

    async transitionRecord(
      planId: string,
      chunkIndex: number,
      transition: (record: AuditRecord) => AuditRecord | null,
    ): Promise<boolean> {
      const db = await openDatabase();

      return new Promise<boolean>((resolve, reject) => {
        let transaction: IDBTransaction;
        let wrote = false;

        try {
          transaction = db.transaction(BASIL_AUDIT_STORE, 'readwrite');
        } catch (error) {
          reject(new AuditUnavailableError(`the audit store could not start a transaction: ${String(error)}`));

          return;
        }

        transaction.oncomplete = () => resolve(wrote);
        transaction.onerror = () => reject(new AuditUnavailableError(`the audit write failed: ${String(transaction.error)}`));
        transaction.onabort = () => reject(new AuditUnavailableError(`the audit write was aborted: ${String(transaction.error)}`));

        try {
          const store = transaction.objectStore(BASIL_AUDIT_STORE);
          const request = store.get([planId, chunkIndex] as unknown as IDBValidKey) as IDBRequest<AuditRecord | undefined>;

          request.onerror = () => reject(new AuditUnavailableError(`the audit store rejected a read: ${String(request.error)}`));
          request.onsuccess = () => {
            const current = request.result;

            if (!current) {
              return;
            }
            // The row the transition sees is the row the store holds NOW, inside this transaction.
            const next = transition(current);

            if (!next || next === current) {
              return;
            }
            store.put(next);
            wrote = true;
          };
        } catch (error) {
          reject(new AuditUnavailableError(`the audit record was rejected: ${String(error)}`));
        }
      });
    },

    listAll(): Promise<AuditRecord[]> {
      return readAll();
    },

    async listUnresolved(): Promise<AuditRecord[]> {
      const rows = await readAll();

      return rows.filter(
        (row) => row.state === 'pending' || row.state === 'sent' || row.state === 'unknown',
      );
    },

    async clearExported(): Promise<number> {
      const rows = await readAll();
      // Only the settled states. An `unknown` outcome is never removed by an export; an entry a
      // writer has attested to (S-3) is, because the attestation rides out in that same export.
      const removable = rows.filter(
        (row) => row.state === 'reconciled' || row.state === 'refused' || row.state === 'settled',
      );
      const db = await openDatabase();

      await new Promise<void>((resolve, reject) => {
        const transaction = db.transaction(BASIL_AUDIT_STORE, 'readwrite');

        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(new AuditUnavailableError(`the audit clear failed: ${String(transaction.error)}`));
        transaction.onabort = () => reject(new AuditUnavailableError(`the audit clear was aborted: ${String(transaction.error)}`));

        const store = transaction.objectStore(BASIL_AUDIT_STORE);

        removable.forEach((row) => {
          store.delete([row.planId, row.chunkIndex] as unknown as IDBValidKey);
        });
      });

      return removable.length;
    },

    close(): void {
      if (database) {
        database.close();
        database = null;
        open = null;
      }
    },
  };
}
